<div class="date-time-picker-container" [class.open]="isOpen()">
  <div
    class="input-wrapper"
    (click)="togglePicker()"
    (keydown.enter)="togglePicker()"
    tabindex="0"
    role="button"
    aria-haspopup="dialog"
    [attr.aria-expanded]="isOpen()"
  >
    <input
      type="text"
      [placeholder]="placeholder"
      [value]="formattedValue()"
      readonly
      class="date-input"
      tabindex="-1"
    />
    <svg class="calendar-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>

    @if (value()) {
      <button type="button" class="clear-btn" (click)="clearValue($event)" aria-label="Clear date">
        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    }
  </div>

  @if (isOpen()) {
    <div
      class="picker-dropdown"
      [class.align-right]="dropdownAlign === 'right'"
      (click)="$event.stopPropagation()"
      (keydown.escape)="closePicker()"
      role="dialog"
      aria-label="Calendar and Time"
    >
      <div class="picker-content-wrapper">
        <!-- Date Section -->
        <div class="date-section">
          <div class="picker-header">
            <button type="button" class="nav-btn" (click)="prevPage()" aria-label="Previous">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button type="button" class="view-label-btn" (click)="toggleMode()">
              @if (mode() === 'days') {
                {{ monthNames[viewDate().getMonth()] }} {{ viewDate().getFullYear() }}
              } @else if (mode() === 'months') {
                {{ viewDate().getFullYear() }}
              } @else {
                {{ yearRangeStart() }} - {{ yearRangeStart() + 11 }}
              }
            </button>

            <button type="button" class="nav-btn" (click)="nextPage()" aria-label="Next">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          @if (mode() === 'days') {
            <div class="weekdays" role="row">
              @for (day of weekDays; track day) {
                <div class="weekday" role="columnheader">{{ day }}</div>
              }
            </div>

            <div class="days-grid">
              @for (day of calendarDays(); track day.date.getTime()) {
                <div
                  class="day-cell"
                  [class.current-month]="day.currentMonth"
                  [class.today]="day.isToday"
                  [class.selected]="isSelected(day.date)"
                  [class.disabled]="isDisabled(day.date)"
                  (click)="selectDate(day.date)"
                  (keydown.enter)="selectDate(day.date)"
                  [tabindex]="day.currentMonth && !isDisabled(day.date) ? 0 : -1"
                  role="gridcell"
                  [aria-selected]="isSelected(day.date)"
                  [attr.aria-label]="day.date.toDateString()"
                >
                  {{ day.date.getDate() }}
                </div>
              }
            </div>
          } @else if (mode() === 'months') {
            <div class="selection-grid months">
              @for (month of monthNames; track month; let i = $index) {
                <button
                  type="button"
                  class="selection-cell"
                  [class.selected]="viewDate().getMonth() === i"
                  (click)="selectMonth(i)"
                >
                  {{ month.substring(0, 3) }}
                </button>
              }
            </div>
          } @else if (mode() === 'years') {
            <div class="selection-grid years">
              @for (year of yearOptions(); track year) {
                <button
                  type="button"
                  class="selection-cell"
                  [class.selected]="viewDate().getFullYear() === year"
                  (click)="selectYear(year)"
                >
                  {{ year }}
                </button>
              }
            </div>
          }
        </div>

        <!-- Time Section -->
        <div class="time-section">
          <div class="time-header">Time</div>
          <div class="time-controls">
            <div class="time-column">
              <div class="column-label">Hour</div>
              <div class="scroll-list">
                @for (h of hours; track h) {
                  <button
                    type="button"
                    class="time-btn"
                    [class.active]="selectedHour() === h"
                    (click)="selectHour(h)"
                  >
                    {{ h.toString().padStart(2, '0') }}
                  </button>
                }
              </div>
            </div>
            <div class="time-column">
              <div class="column-label">Min</div>
              <div class="scroll-list">
                @for (m of minutes; track m) {
                  <button
                    type="button"
                    class="time-btn"
                    [class.active]="selectedMinute() === m"
                    (click)="selectMinute(m)"
                  >
                    {{ m.toString().padStart(2, '0') }}
                  </button>
                }
              </div>
            </div>
            <div class="time-column">
              <div class="column-label">Period</div>
              <div class="scroll-list">
                <button
                  type="button"
                  class="time-btn"
                  [class.active]="selectedPeriod() === 'AM'"
                  (click)="setPeriod('AM')"
                >
                  AM
                </button>
                <button
                  type="button"
                  class="time-btn"
                  [class.active]="selectedPeriod() === 'PM'"
                  (click)="setPeriod('PM')"
                >
                  PM
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="picker-footer">
        <button type="button" class="footer-btn now-btn" (click)="setNow()">Now</button>
        <button type="button" class="footer-btn clear-footer-btn" (click)="clearValue()">
          Clear
        </button>
      </div>
    </div>
  }
</div>
